import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { IoSearch } from "react-icons/io5";
import { useCallback, useEffect, useState } from "react";
import { fetchWatchedBuilders, removeWatchedBuilder } from "../services/watchBuilderService";
import { useLoading } from "@/contexts/LoadingContext";
import useDebounce from "@/hooks/useDebounce";
// import unwatchIcon from "@/assets/myaccount/remove-watchlist.svg";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import noResultFound from "@/assets/projectlisiting/no-results.svg";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface PaginationData {
  page: number;
  limit: number;
  nextPage: number;
}

export interface WatchedBuilder {
  id: number;
  email: string;
  name: string;
  phone: string;
  builder_company: string;
}

const WatchBuilders = () => {
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 10,
    nextPage: -1,
  });
  const [watchedBuilders, setWatchedBuilders] = useState<WatchedBuilder[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { setLoading } = useLoading();
  const debouncedSearchTerm = useDebounce(searchTerm);

  const fetchWatchList = useCallback(
    async (query = "", page = pagination.page) => {
      try {
        setLoading(true);
        const response = await fetchWatchedBuilders({
          page,
          limit: pagination.limit,
          q: query,
        });
        setWatchedBuilders(response?.data?.builders || []);
        setPagination((prev) => ({
          ...prev,
          page,
          nextPage: response?.data?.nextPage ?? -1,
        }));
      } catch (error) {
        console.error("Error fetching watchlist data:", error);
        setWatchedBuilders([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, setLoading]
  );

  const unwatchBuilder = useCallback(
    async (builder_id: number) => {
      try {
        console.log("line 73", builder_id);
        console.log("line 74")
        setLoading(true);
        await removeWatchedBuilder(builder_id);
        // After deletion, refetch the watchlist
        fetchWatchList(debouncedSearchTerm, pagination.page);
      } catch (error) {
        console.error("Error removing watched builder:", error);
      } finally {
        setLoading(false);
      }
    }
  , [debouncedSearchTerm, fetchWatchList, pagination.page, setLoading]);

  useEffect(() => {
    fetchWatchList(debouncedSearchTerm, 1);
  }, [fetchWatchList, debouncedSearchTerm]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && (newPage < pagination.page || pagination.nextPage !== -1)) {
      fetchWatchList(debouncedSearchTerm, newPage);
    }
  };

  return (
    <div className="">
      {watchedBuilders.length === 0  && !debouncedSearchTerm? (
        <div className="w-full mx-auto">
        <div className="inset-0 absolute flex flex-col items-center justify-center px-8 py-4">
          <img src={noResultFound} alt="No results found" className="w-1/5" />
          <h2 className="text-[18px] font-semibold text-gray-700">
            Your Builder Watchlist is Empty
          </h2>
          <p className="text-gray-500 mt-2 w-[35%] text-center text-[14px]">
            Keep an eye on the builders you care about.
            Add them to your watchlist to stay updated with their activity.
          </p>
        </div>
        </div>
      ) : (
        <>
          <div className="mx-8">
            <fieldset className="relative w-[23vw] mt-4">
              <Button
                variant="ghost"
                size="sm"
                className="absolute left-1 top-1/2 transform -translate-y-1/2 hover:bg-transparent hover:text-inherit focus:bg-transparent focus:text-inherit"
              >
                <IoSearch size={15} />
              </Button>

              <Input
                type="text"
                placeholder="Search Builder"
                className="w-full pl-10 pr-4 py-1 h-[40px] border rounded"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </fieldset>
          </div>

          <div className="w-full mx-0 mt-4">
            <hr />
          </div>

          <div className="mx-8 rounded-lg overflow-hidden border mt-4 mb-6 border-gray-300">
            <Table>
              <TableHeader className="bg-[#f9fafb]">
                <TableRow>
                  <TableHead className="py-2 px-8 text-left text-[#828282]">Company</TableHead>
                  <TableHead className="p-2 text-left text-[#828282]">Name</TableHead>
                  <TableHead className="p-2 text-left text-[#828282]">Phone</TableHead>
                  <TableHead className="p-2 text-left text-[#828282]">Email</TableHead>
                  <TableHead className="p-2 text-left pl-7 text-[#828282]">Action</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {watchedBuilders.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell className="py-2 px-9 text-left">{entry.builder_company}</TableCell>
                    <TableCell className="p-2 text-left">{entry.name}</TableCell>
                    <TableCell className="p-2 text-left">{entry.phone}</TableCell>
                    <TableCell className="p-2 text-left">{entry.email}</TableCell>
                    <TableCell className="p-2  text-center">
                    <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="default"
        onClick={() => unwatchBuilder(Number(entry.id))}
        className="bg-black hover:bg-black py-0 h-[35px]"
      >
        <span className="text-white">Unwatch</span>
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top" align="end">
      Remove builder from watchlist
    </TooltipContent>
  </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              <tfoot>
                <TableRow className="border-t">
                  <TableCell colSpan={5}>
                    <div className="flex justify-center items-center">
                      <div className="flex items-center space-x-4">
                        <Button
                          variant="outline"
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          <FaChevronLeft />
                        </Button>
                        <span className="text-gray-700 font-medium">
                          Page {pagination.page}
                        </span>
                        <Button
                          variant="outline"
                          disabled={pagination.nextPage === -1 || watchedBuilders.length === 0 }
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          <FaChevronRight />
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </tfoot>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default WatchBuilders;
